<template>
  <div class="image-uploader-container">
    <!-- 图片上传框列表 -->
    <div class="upload-box-list">
      <!-- 已上传的图片框 -->
      <div
          v-for="(url, index) in imageUrls"
          :key="index"
          :style="{ width: boxWidth + 'px', height: boxHeight + 'px' }"
          class="upload-box"
      >
        <img
            :src="url"
            alt="上传的图片"
            class="preview-img"
            @dblclick="handleImageDblClick(url)"
        >
        <div v-if="onlyPreview === false" class="delete-btn" @click.stop="handleDelete(index)">
          <delete-outlined />
        </div>
      </div>

      <!-- 新增上传框 -->
      <div
          v-if="onlyPreview === false && imageUrls.length < maxCount"
          :style="{ width: boxWidth + 'px', height: boxHeight + 'px' }"
          class="upload-box upload-area"
          @click="handleUploadClick"
      >
        <div class="upload-hint">
          <plus-outlined class="plus-icon" />
          <p class="hint-text">点击上传图片</p>
        </div>
      </div>
    </div>

    <!-- 图片预览弹窗 -->
    <a-modal
        v-model:open="previewVisible"
        :footer="null"
        :width="800"
    >
      <div class="preview-modal-content">
        <img :src="previewImageUrl" alt="预览图片" class="modal-image">
      </div>
    </a-modal>

    <!-- 隐藏的上传组件 -->
    <a-upload
        name="file"
        :action="''"
        :beforeUpload="beforeUpload"
        :showUploadList="false"
        accept=".jpg,.jpeg,.png,.gif"
        ref="uploadRef"
        :multiple="false"
    />
  </div>
</template>

<script setup>
import {ref, defineProps, defineEmits, watch, defineExpose} from 'vue';
import { Upload, message } from 'ant-design-vue';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons-vue';
import { uploadOneFile } from '@/api/upload.js';

// 定义属性
const props = defineProps({
  // 图片路径数组
  imageUrls: {
    type: Array,
    default: () => []
  },
  // 上传路径名称
  pathName: {
    type: String,
    default: 'image'
  },
  // 最大上传数量
  maxCount: {
    type: Number,
    default: 5
  },
  // 上传框宽度
  boxWidth: {
    type: Number,
    default: 150
  },
  // 上传框高度
  boxHeight: {
    type: Number,
    default: 150
  },
  // 是否仅预览
  onlyPreview: {
    type: Boolean,
    default: false
  }

});

// 定义事件
const emits = defineEmits(['update:imageUrls', 'uploadSuccess', 'delete']);

// 引用
const uploadRef = ref(null);
const localImageUrls = ref([...props.imageUrls]);

// 预览相关状态
const previewVisible = ref(false);
const previewImageUrl = ref('');

// 处理上传点击
const handleUploadClick = () => {
  if (uploadRef.value && localImageUrls.value.length < props.maxCount) {
    // 触发文件输入框的点击事件
    uploadRef.value.$el.querySelector('input[type="file"]').click();
  } else if (localImageUrls.value.length >= props.maxCount) {
    message.warning(`最多只能上传${props.maxCount}张图片`);
  }
};

// 处理删除
const handleDelete = (index) => {
  const newUrls = [...localImageUrls.value];
  newUrls.splice(index, 1);
  localImageUrls.value = newUrls;
  emits('update:imageUrls', newUrls);
  emits('delete', index);
};

// 处理图片双击事件
const handleImageDblClick = (url) => {
  previewImageUrl.value = url;
  previewVisible.value = true;
};

// 上传前处理
const beforeUpload = (file) => {
  // 检查文件类型
  const isImage = /\.(jpg|jpeg|png|gif)$/i.test(file.name);
  if (!isImage) {
    message.error('仅支持 JPG、JPEG、PNG、GIF 格式的图片!');
    return Upload.LIST_IGNORE;
  }

  // 检查文件大小
  const isLt2M = file.size / 1024 / 1024 < 2;
  if (!isLt2M) {
    message.error('每张图片大小不能超过 2MB!');
    return Upload.LIST_IGNORE;
  }

  // 检查是否超过最大数量
  if (localImageUrls.value.length >= props.maxCount) {
    message.warning(`最多只能上传${props.maxCount}张图片`);
    return Upload.LIST_IGNORE;
  }

  // 上传文件
  const formData = convertFormData(file);
  uploadOneFile(formData)
      .then(res => {
        if (res && res.data) {
          const newUrls = [...localImageUrls.value, res.data];
          localImageUrls.value = newUrls;
          emits('update:imageUrls', newUrls);
          emits('uploadSuccess', res.data, newUrls);
          message.success('上传成功');
        }
      })
      .catch(err => {
        message.error(`上传失败: ${err.message || '未知错误'}`);
      });

  return false; // 阻止自动上传
};

// 转换为FormData
const convertFormData = (file) => {
  const folder = Date.now().toString();
  const json = {
    bucketName: 'testing-management-public',
    pathName: props.pathName + '/' + folder, // 上传服务器路径
  };
  const formData = new FormData();
  formData.append('uploadFile', file);
  formData.append('json', new Blob([JSON.stringify(json)], { type: 'application/json' }));
  return formData;
};

// 监听外部传入的imageUrls变化
watch(() => props.imageUrls, (newVal) => {
  localImageUrls.value = [...(newVal || [])];
});

// 导出组件引用
defineExpose({
  handleDelete,
  clearAll: () => {
    localImageUrls.value = [];
    emits('update:imageUrls', []);
  }
});
</script>

<style lang="less" scoped>
.image-uploader-container {
  .upload-box-list {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }

  .upload-box {
    position: relative;
    border: 2px dashed #d9d9d9;
    border-radius: 8px;
    overflow: hidden;
    transition: all 0.3s;

    &:hover {
      border-color: #1890ff;
    }

    .preview-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      cursor: pointer; /* 提示用户图片可交互 */
      transition: transform 0.3s;

      &:hover {
        transform: scale(1.05); /* 鼠标悬停时轻微放大 */
      }
    }

    .delete-btn {
      position: absolute;
      top: 5px;
      right: 5px;
      width: 24px;
      height: 24px;
      background-color: rgba(0, 0, 0, 0.5);
      color: white;
      border-radius: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;

      &:hover {
        background-color: rgba(0, 0, 0, 0.7);
      }
    }
  }

  .upload-area {
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
  }

  .upload-hint {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 10px;

    .plus-icon {
      font-size: 32px;
      color: #1890ff;
      margin-bottom: 8px;
    }

    .hint-text {
      color: #999;
      font-size: 14px;
      text-align: center;
    }
  }
}

/* 预览弹窗样式 */
.preview-modal-content {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;

  .modal-image {
    max-width: 100%;
    max-height: 600px;
    object-fit: contain;
  }
}
</style>